import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { developerInfo, asciiArt, projects, articles, skills } from '../mock';

// Matrix Easter Egg Component
const MatrixEasterEgg = ({ onClose }) => {
  const [displayText, setDisplayText] = useState('');
  const [showCursor, setShowCursor] = useState(true);
  const [phase, setPhase] = useState(0);
  
  const messages = [
    "Wake up, Neo...",
    "The Matrix has you...",
    "Follow the white rabbit.",
    "Knock, knock, Neo."
  ];

  useEffect(() => {
    if (phase >= messages.length) {
      const timeout = setTimeout(() => {
        onClose();
      }, 2000);
      return () => clearTimeout(timeout);
    }

    const message = messages[phase];
    let charIndex = 0;
    setDisplayText('');

    const typeInterval = setInterval(() => {
      if (charIndex < message.length) {
        setDisplayText(message.substring(0, charIndex + 1));
        charIndex++;
      } else {
        clearInterval(typeInterval);
        setTimeout(() => {
          setPhase(phase + 1);
        }, 1500);
      }
    }, 100);

    return () => clearInterval(typeInterval);
  }, [phase, onClose]);

  useEffect(() => {
    const cursorInterval = setInterval(() => {
      setShowCursor(prev => !prev);
    }, 530);
    return () => clearInterval(cursorInterval);
  }, []);

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [onClose]);

  return (
    <div 
      className="matrix-overlay"
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: '#000',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 99999,
        cursor: 'pointer'
      }}
    >
      <div style={{
        fontFamily: '"Courier New", monospace',
        fontSize: 'clamp(1.2rem, 3vw, 2rem)',
        color: '#00ff00',
        textShadow: '0 0 10px #00ff00, 0 0 20px #00ff00, 0 0 30px #00ff00',
        textAlign: 'center',
        padding: '20px'
      }}>
        {displayText}
        <span style={{ 
          opacity: showCursor ? 1 : 0,
          marginLeft: '2px'
        }}>█</span>
      </div>
    </div>
  );
};

const Terminal = ({ fullScreen = false }) => {
  const [input, setInput] = useState('');
  const [history, setHistory] = useState([]);
  const [commandHistory, setCommandHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [currentPath, setCurrentPath] = useState('~');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showMatrix, setShowMatrix] = useState(false);
  const [sudoAttempts, setSudoAttempts] = useState(0);
  const inputRef = useRef(null);
  const outputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (fullScreen && history.length === 0) {
      const welcomeArt = isMobile ? asciiArt.welcomeMobile : asciiArt.welcome;
      
      setHistory([{
        command: '',
        output: welcomeArt + '\n\nType "help" for available commands.\n'
      }]);
    }
  }, [fullScreen, isMobile]);

  useEffect(() => {
    if (outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  }, [history]);

  // Focus input when matrix closes
  useEffect(() => {
    if (!showMatrix && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showMatrix]);

  const simulateNmap = (target) => {
    const ip = target || '192.168.1.1';
    return `${asciiArt.nmap}
Nmap scan report for ${ip}
Host is up (0.00042s latency).
Not shown: 997 closed ports
PORT     STATE SERVICE
22/tcp   open  ssh
80/tcp   open  http
443/tcp  open  https

Nmap done: 1 IP address (1 host up) scanned in 2.34 seconds`;
  };

  const simulateHydra = (target) => {
    return `${asciiArt.hydra}Hydra (https://github.com/vanhauser-thc/thc-hydra)

[DATA] max 16 tasks per 1 server, overall 16 tasks, 100 login tries
[DATA] attacking ssh://${target || 'target.com'}:22/
[22][ssh] host: ${target || 'target.com'}   login: admin   password: password123
[STATUS] attack finished for ${target || 'target.com'} (valid pair found)
1 of 1 target successfully completed, 1 valid password found`;
  };

  const simulateMetasploit = () => {
    return `${asciiArt.metasploit}
msf6 > use exploit/multi/handler
[*] Using configured payload generic/shell_reverse_tcp
msf6 exploit(multi/handler) > set LHOST 0.0.0.0
LHOST => 0.0.0.0
msf6 exploit(multi/handler) > set LPORT 4444
LPORT => 4444
msf6 exploit(multi/handler) > exploit

[*] Started reverse TCP handler on 0.0.0.0:4444
[*] Command shell session 1 opened

Type 'exit' to close session...`;
  };

  const simulateFfuf = (url) => {
    return `        /'___\\  /'___\\           /'___\\       
       /\\ \\__/ /\\ \\__/  __  __  /\\ \\__/       
       \\ \\ ,__\\\\ \\ ,__\\/\\ \\/\\ \\ \\ ,__\\      
        \\ \\ \\_/ \\ \\ \\_/\\ \\ \\_\\ \\ \\ \\_/      
         \\ \\_\\   \\ \\_\\  \\ \\____/ \\ \\_\\       
          \\/_/    \\/_/   \\/___/   \\/_/       

       v2.0.0-dev
________________________________________________

 :: Method           : GET
 :: URL              : ${url || 'https://target.com'}/FUZZ
 :: Wordlist         : FUZZ: /usr/share/wordlists/common.txt
 :: Follow redirects : false
 :: Status codes     : 200,204,301,302,307,401,403
________________________________________________

admin                   [Status: 301, Size: 234, Words: 14, Lines: 8]
api                     [Status: 200, Size: 1024, Words: 89, Lines: 23]
login                   [Status: 200, Size: 2456, Words: 234, Lines: 67]

:: Progress: [4713/4713] :: Job [1/1] :: 523 req/sec :: Duration: [0:00:09]`;
  };

  const simulateWafwoof = (url) => {
    return `                   ______
                  /      \\
                 (  W00f! )
                  \\______/
        ______________|______________
   ____|                             |____
  /    \\                             /    \\
 /      |           WAFWOOF           |      \\
/       |                             |       \\

WAFW00F - Web Application Firewall Detection Tool

Checking ${url || 'https://target.com'}

[*] The site ${url || 'https://target.com'} is behind a WAF
[~] Cloudflare (Cloudflare Inc.)
[~] Number of requests: 6`;
  };

  const simulateDocker = (args) => {
    if (args.includes('ps')) {
      return `CONTAINER ID   IMAGE          COMMAND                  CREATED        STATUS        PORTS     NAMES
a1b2c3d4e5f6   nginx:latest   "/docker-entrypoint.…"   2 hours ago    Up 2 hours    80/tcp    webserver
b2c3d4e5f6a7   postgres:14    "docker-entrypoint.s…"   3 hours ago    Up 3 hours    5432/tcp  database`;
    } else if (args.includes('images')) {
      return `REPOSITORY    TAG       IMAGE ID       CREATED        SIZE
nginx         latest    a1b2c3d4e5f6   2 days ago     142MB
postgres      14        b2c3d4e5f6a7   3 days ago     376MB
python        3.11      c3d4e5f6a7b8   1 week ago     917MB`;
    }
    return `Docker version 24.0.7, build afdd53b\n\nUsage:  docker [OPTIONS] COMMAND\n\nA self-sufficient runtime for containers`;
  };

  // Random insults for wrong commands
  const getRandomInsult = () => {
    const insults = [
      "Nice try, script kiddie.",
      "Did you even read the manual?",
      "That's not how any of this works.",
      "Error 40: User located between keyboard and chair.",
      "Have you tried turning yourself off and on again?",
      "That command is as useful as a screen door on a submarine.",
      "I've seen better attempts from a cat walking on a keyboard.",
      "This isn't the terminal you're looking for. *waves hand*"
    ];
    return insults[Math.floor(Math.random() * insults.length)];
  };

  // Sudo responses
  const getSudoResponse = (attempts) => {
    const responses = [
      "Password: ******\n[sudo] wrong password for neo\nNice try, but you're not root here.",
      "Password: ******\n[sudo] user 'gabriele' is not in the sudoers file. This incident will be reported.",
      "Password: ******\nAccess denied. Did you really think that would work?",
      "Password: ******\nroot privileges? In this economy?",
      "Password: ******\n🚨 ALERT: Suspicious activity detected. Your IP has been logged. Just kidding. Or am I?",
    ];
    return responses[Math.min(attempts, responses.length - 1)];
  };

  // Coffee ASCII art
  const coffeeArt = `
        ( (
         ) )
      ........
      |      |]
      \\      /
       '----'
   
  Here's your coffee ☕
  Now get back to work!`;

  // Hacker ASCII art
  const hackerArt = `
    ⠀⠀⠀⠀⠀⠀⠀⢀⣀⣀⣀⣀⣀⣀⡀⠀⠀⠀⠀⠀⠀⠀
    ⠀⠀⠀⠀⠀⢀⣴⣿⣿⣿⣿⣿⣿⣿⣿⣷⣄⠀⠀⠀⠀⠀
    ⠀⠀⠀⠀⣴⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣷⡀⠀⠀⠀
    ⠀⠀⠀⣼⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣧⠀⠀⠀
    ⠀⠀⢰⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⡄⠀⠀
    ⠀⠀⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⠀⠀
    ⠀⢀⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⡀⠀
    ⠀⢸⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⡇⠀
    ⠀⢸⣿⣿⡿⠿⠿⠿⠿⠿⠿⠿⠿⠿⠿⠿⠿⢿⣿⣿⡇⠀
    ⠀⠘⠉⠁⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠈⠉⠃⠀
    
  "I'm in." - Every movie hacker ever`;

  const executeCommand = useCallback((cmd) => {
    const trimmedCmd = cmd.trim();
    const lowerCmd = trimmedCmd.toLowerCase();
    const [command, ...args] = trimmedCmd.split(' ');
    const argsStr = args.join(' ');

    let output = '';

    // Check for dangerous/malicious commands - trigger Matrix Easter Egg
    const dangerousCommands = [
      'rm -rf /',
      'rm -rf /*',
      'rm -rf ~',
      'rm -rf .',
      ':(){ :|:& };:',
      'fork bomb',
      'dd if=/dev/zero',
      'mkfs.ext4 /dev/sda',
      'chmod -r 777 /',
      '> /dev/sda',
      'mv / /dev/null',
      'wget malware',
      'curl malware',
      'nc -e /bin/bash',
      'python -c "import os; os.system(\'rm -rf /\')"'
    ];

    if (dangerousCommands.some(dc => lowerCmd.includes(dc.toLowerCase()))) {
      setShowMatrix(true);
      setHistory([...history, { command: trimmedCmd, output: '', path: currentPath }]);
      setCommandHistory([...commandHistory, trimmedCmd]);
      setHistoryIndex(-1);
      return;
    }

    switch (command.toLowerCase()) {
      case '':
        break;
      
      case 'help':
        output = `Available commands:

Navigation:
  ls                    - List available pages
  cd <page>            - Navigate to page (e.g., cd ./home, cd ./about)
  pwd                  - Print working directory

Information:
  whoami               - Display developer info
  cat <file>           - Display file contents (skills, projects, articles)
  echo <text>          - Print text to terminal
  man <cmd>            - Manual page (sort of)

Utility:
  clear                - Clear terminal screen
  history              - Show command history
  date                 - Display current date and time
  uptime               - System uptime
  cowsay <text>        - Make a cow say something
  fortune              - Get a random fortune
  coffee               - ☕

Security Tools (Demo):
  nmap <target>        - Network scanner
  hydra <target>       - Password cracker
  metasploit           - Exploitation framework
  ffuf <url>           - Web fuzzer
  wafwoof <url>        - WAF detection tool
  docker <args>        - Container management
  hack                 - ???

Easter Eggs:
  There are some hidden commands... try to find them! 🥚

Tip: Use arrow keys to navigate command history`;
        break;

      case 'ls':
        if (argsStr === '-la' || argsStr === '-l' || argsStr === '-al') {
          output = `total 42
drwxr-xr-x   7 gabriele  staff    224 Jan  3 2026  .
drwxr-xr-x   3 root      root      96 Jan  3 2026  ..
-rw-------   1 gabriele  staff   1337 Jan  3 2026  .bash_history
-rw-r--r--   1 gabriele  staff    420 Jan  3 2026  .bashrc
drwx------   2 gabriele  staff     64 Jan  3 2026  .ssh
drwxr-xr-x   2 gabriele  staff     64 Jan  3 2026  home/
drwxr-xr-x   2 gabriele  staff     64 Jan  3 2026  about/
drwxr-xr-x   2 gabriele  staff     64 Jan  3 2026  projects/
drwxr-xr-x   2 gabriele  staff     64 Jan  3 2026  blog/
drwxr-xr-x   2 gabriele  staff     64 Jan  3 2026  contacts/
-rw-r--r--   1 gabriele  staff    512 Jan  3 2026  README.md
-rw-r--r--   1 gabriele  staff     69 Jan  3 2026  .secret`;
        } else if (argsStr === '-a') {
          output = `.  ..  .bash_history  .bashrc  .ssh  .secret  home/  about/  projects/  blog/  contacts/  README.md`;
        } else {
          output = `total 5
drwxr-xr-x  2 gabriele  staff   64 Jan  3 2026  home/
drwxr-xr-x  2 gabriele  staff   64 Jan  3 2026  about/
drwxr-xr-x  2 gabriele  staff   64 Jan  3 2026  projects/
drwxr-xr-x  2 gabriele  staff   64 Jan  3 2026  blog/
drwxr-xr-x  2 gabriele  staff   64 Jan  3 2026  contacts/
-rw-r--r--  1 gabriele  staff  512 Jan  3 2026  README.md`;
        }
        break;

      case 'cd':
        const page = argsStr.replace('./', '').replace('/', '');
        const validPages = ['home', 'about', 'projects', 'blog', 'contacts'];
        
        if (!page || page === '~') {
          setCurrentPath('~');
          navigate('/');
          output = '';
        } else if (page === '..') {
          output = "...nothing to see here 👀";
        } else if (page === '-') {
          output = "bash: cd: OLDPWD not set\n(Nice try though, time traveler)";
        } else if (page === '/') {
          output = "Permission denied: You shall not pass! 🧙";
        } else if (page === '.ssh' || page === '.secret') {
          output = "🔐 Access denied. Some secrets are meant to stay secret.";
        } else if (validPages.includes(page.toLowerCase())) {
          setCurrentPath(`~/${page}`);
          navigate(`/${page}`);
          output = '';
        } else {
          output = `cd: ${page}: No such file or directory\n(But nice imagination!)`;
        }
        break;

      case 'pwd':
        output = `/home/gabriele${currentPath === '~' ? '' : currentPath.replace('~', '')}`;
        break;

      case 'whoami':
        output = `${developerInfo.name}
${developerInfo.role}
${developerInfo.location}
${developerInfo.vat}

${developerInfo.bio}

"Not all those who wander are lost, but I definitely use GPS."`;
        break;

      case 'cat':
        if (argsStr.includes('skills')) {
          output = `Technical Skills:\n\n${skills.join(', ')}`;
        } else if (argsStr.includes('projects')) {
          output = projects.map(p => 
            `${p.name}\n${p.description}\nTech: ${p.tech.join(', ')}\nGitHub: ${p.github}\n`
          ).join('\n');
        } else if (argsStr.includes('articles') || argsStr.includes('blog')) {
          output = articles.map(a => 
            `[${a.date}] ${a.title}\n${a.excerpt}\nTags: ${a.tags.join(', ')} | ${a.readTime}\n`
          ).join('\n');
        } else if (argsStr.includes('README') || argsStr.includes('readme')) {
          output = `# Gabriele Tita - Portfolio

Welcome to my personal website!

I'm a backend developer and cybersecurity specialist based in Verona, Italy.
This site showcases my projects, articles, and professional experience.

Feel free to explore using the terminal or navigation menu.

P.S. Try some Easter egg commands... if you dare 🥚`;
        } else if (argsStr.includes('.secret')) {
          output = `🎉 Congratulations! You found the secret file!

Here's a secret: The cake is a lie.
Also, Vim users and Emacs users can be friends.
And tabs vs spaces? The real answer is: whatever your team agrees on.

Now go touch some grass. 🌱`;
        } else if (argsStr.includes('.bash_history')) {
          output = `sudo apt-get install coffee
vim .vimrc
git commit -m "fixed a bug"
git commit -m "fixed the fix"
git commit -m "why is this not working"
git commit -m "AAAAAAHHHHH"
git push --force
rm -rf node_modules && npm install
stackoverflow "how to exit vim"`;
        } else if (argsStr === '/dev/urandom') {
          output = "☠️⚡️🔥💀👻🎃🦇🕷️🌙✨🔮🎭💫🌈🦄🍕🎸🚀🌍💾🖥️🎮🕹️📱💡🔧⚙️🛡️🔐🗝️";
        } else if (!argsStr) {
          output = "cat: Meow? 🐱\n(Hint: specify a file to read)";
        } else {
          output = `cat: ${argsStr}: No such file or directory`;
        }
        break;

      case 'echo':
        if (!argsStr) {
          output = '';
        } else if (argsStr.includes('$PATH')) {
          output = '/usr/local/bin:/usr/bin:/bin:/usr/sbin:/sbin:/home/gabriele/.local/bin';
        } else if (argsStr.includes('$HOME')) {
          output = '/home/gabriele';
        } else if (argsStr.includes('$USER')) {
          output = 'gabriele';
        } else if (argsStr.includes('$SHELL')) {
          output = '/bin/bash';
        } else if (argsStr.toLowerCase().includes('hello world')) {
          output = `${argsStr}\n\n🎉 Ah, a classic! You know the sacred traditions.`;
        } else {
          output = argsStr;
        }
        break;

      case 'clear':
        setHistory([]);
        return;

      case 'history':
        output = commandHistory.length > 0 
          ? commandHistory.map((cmd, i) => `  ${i + 1}  ${cmd}`).join('\n')
          : "No commands in history yet. You're a blank slate!";
        break;

      case 'date':
        output = new Date().toString();
        break;

      case 'uptime':
        output = ` ${new Date().toLocaleTimeString()}  up 42 days, 13:37,  1 user,  load average: 0.42, 0.69, 1.337`;
        break;

      case 'man':
        if (!argsStr) {
          output = "What manual page do you want?\nFor example: man bash";
        } else if (argsStr === 'man') {
          output = "Yo dawg, I heard you like manuals...";
        } else if (argsStr === 'woman') {
          output = "No manual entry for woman.\n(Did you really think that would work?)";
        } else {
          output = `No manual entry for ${argsStr}

But here's some life advice instead:
- Drink water
- Take breaks
- Comment your code
- Be kind to yourself`;
        }
        break;

      case 'sudo':
        const newAttempts = sudoAttempts + 1;
        setSudoAttempts(newAttempts);
        output = getSudoResponse(newAttempts - 1);
        break;

      case 'su':
        output = "su: Authentication failure\n\nYou're not root. Accept it. Embrace it. Become one with your limited permissions.";
        break;

      case 'exit':
      case 'logout':
      case 'quit':
        output = "There is no escape. You're here forever now. 👁️\n\n(Just kidding, use the navigation menu or close the tab)";
        break;

      case 'vim':
      case 'vi':
        output = `
You have entered Vim. Good luck escaping.

Just kidding! Here are some exit commands:
:q!  - Quit without saving
:wq  - Save and quit
:x   - Save and quit (shorter)
ZZ   - Save and quit (even shorter)

Or just close the browser tab. No judgment here.`;
        break;

      case 'emacs':
        output = "Emacs? In this economy? Real hackers use ed.";
        break;

      case 'nano':
        output = "Ah, nano. The training wheels of text editors. No shame in that! 🚲";
        break;

      case 'cowsay':
        const cowText = argsStr || "Moo!";
        const border = '_'.repeat(cowText.length + 2);
        output = ` ${border}
< ${cowText} >
 ${'-'.repeat(cowText.length + 2)}
        \\   ^__^
         \\  (oo)\\_______
            (__)\\       )\\/\\
                ||----w |
                ||     ||`;
        break;

      case 'fortune':
        const fortunes = [
          "You will find bugs in the most unexpected places.",
          "A semicolon will save your day. Or ruin it.",
          "The code you write today is the legacy code of tomorrow.",
          "There are only two hard things in CS: cache invalidation, naming things, and off-by-one errors.",
          "It works on my machine! ¯\\_(ツ)_/¯",
          "Git blame reveals all truths.",
          "The best code is no code at all.",
          "Coffee is the most important design pattern.",
          "A well-placed console.log is worth a thousand debuggers.",
          "May your builds be green and your deploys be smooth."
        ];
        output = fortunes[Math.floor(Math.random() * fortunes.length)];
        break;

      case 'coffee':
      case 'make':
        if (command === 'make' && argsStr !== 'coffee' && argsStr !== 'me a sandwich') {
          output = `make: *** No rule to make target '${argsStr || 'nothing'}'. Stop.`;
        } else if (argsStr === 'me a sandwich') {
          output = "What? Make it yourself.";
        } else {
          output = coffeeArt;
        }
        break;

      case 'hack':
      case 'hacker':
        output = hackerArt;
        break;

      case 'ping':
        if (!argsStr) {
          output = "ping: usage error: Destination address required";
        } else if (argsStr.includes('localhost') || argsStr.includes('127.0.0.1')) {
          output = `PING ${argsStr} (127.0.0.1): 56 data bytes
64 bytes from 127.0.0.1: icmp_seq=0 ttl=64 time=0.042 ms
64 bytes from 127.0.0.1: icmp_seq=1 ttl=64 time=0.038 ms
64 bytes from 127.0.0.1: icmp_seq=2 ttl=64 time=0.041 ms

--- ${argsStr} ping statistics ---
3 packets transmitted, 3 packets received, 0.0% packet loss
round-trip min/avg/max = 0.038/0.040/0.042 ms

(Yes, you're still alive. Congratulations!)`;
        } else {
          output = `PING ${argsStr}: Name or service not known\n(But A for effort!)`;
        }
        break;

      case 'ifconfig':
      case 'ip':
        output = `lo: flags=73<UP,LOOPBACK,RUNNING>  mtu 65536
        inet 127.0.0.1  netmask 255.0.0.0
        inet6 ::1  prefixlen 128  scopeid 0x10<host>

eth0: flags=4163<UP,BROADCAST,RUNNING,MULTICAST>  mtu 1500
        inet 192.168.1.42  netmask 255.255.255.0  broadcast 192.168.1.255
        inet6 fe80::1337:dead:beef:cafe  prefixlen 64  scopeid 0x20<link>

(No, I won't show you my real IP. Nice try though 😏)`;
        break;

      case 'neofetch':
      case 'screenfetch':
        output = `
        .-/+oossssoo+/-.               gabriele@portfolio
    \`:+ssssssssssssssssss+:\`           -------------------
  -+ssssssssssssssssssyyssss+-         OS: ReactOS 2025.1
 .osssssssssssssssssssdMMMNysssso.     Host: Your Browser
 /ssssssssssshdmmNNmmyNMMMMhssssss/    Kernel: JavaScript
+ssssssssshmydMMMMMMMNddddyssssssss+   Uptime: ∞
/sssssssshNMMMyhhyyyyhmNMMMNhssssssss/ Shell: Fake Bash
.ssssssssdMMMNhsssssssssshNMMMdssssssss.Terminal: Web Terminal
+sssshhhyNMMNyssssssssssssyNMMMysssssss+CPU: 100% Caffeine
ossyNMMMNyMMhsssssssssssssshmmmhssssssso Memory: npm ate it all
ossyNMMMNyMMhsssssssssssssshmmmhssssssso
+sssshhhyNMMNyssssssssssssyNMMMysssssss+
.ssssssssdMMMNhsssssssssshNMMMdssssssss.
 /sssssssshNMMMyhhyyyyhdNMMMNhssssssss/
  +sssssssssdmydMMMMMMMMddddyssssssss+
   /ssssssssssshdmNNNNmyNMMMMhssssss/
    .ossssssssssssssssssdMMMNysssso.
      -+sssssssssssssssssyyyssss+-
        \`:+ssssssssssssssssss+:\`
            .-/+oossssoo+/-.`;
        break;

      case 'sl':
        output = `
    ====        ________                ___________
_D _|  |_______/        \\__I_I_____===__|_________|
 |(_)---  |   H\\________/ |   |        =|___ ___|  
 /     |  |   H  |  |     |   |         ||_| |_||   
|      |  |   H  |__--------------------| [___] |   
| ________|___H__/__|_____/[][]~\\_______|       |   
|/ |   |-----------I_____I [][] []  D   |=======|__
__/ =| o |=-~~\\  /~~\\  /~~\\  /~~\\ ____Y___________|__
|/-=|___|=    ||    ||    ||    |_____/~\\___/       
 \\_/      \\O=====O=====O=====O_/      \\_/            

You meant 'ls', didn't you? 🚂`;
        break;

      case 'rm':
        if (argsStr.includes('-rf') || argsStr.includes('-fr')) {
          // This should trigger the matrix, but just in case...
          output = "Nice try, Neo. 😎";
        } else {
          output = `rm: cannot remove '${argsStr || '*'}': Permission denied\n(And honestly, I'm glad about that)`;
        }
        break;

      case 'touch':
        if (!argsStr) {
          output = "touch: missing file operand";
        } else {
          output = `touch: cannot touch '${argsStr}': Read-only file system\n(This is a demo terminal, remember?)`;
        }
        break;

      case 'mkdir':
        output = `mkdir: cannot create directory '${argsStr || 'undefined'}': Read-only file system\n(Nice try, but this isn't a real shell 😉)`;
        break;

      case 'grep':
        if (!argsStr) {
          output = "Usage: grep [PATTERN] [FILE]...";
        } else {
          output = `grep: ${argsStr}: No such file or directory\n(But if you're looking for bugs, check node_modules)`;
        }
        break;

      case 'find':
        output = "find: I found myself, and it was a journey. 🧘\n(To find files, maybe try 'ls' first?)";
        break;

      case 'top':
      case 'htop':
        output = `top - ${new Date().toLocaleTimeString()} up 42 days, 13:37 load average: 0.42, 0.69, 1.337
Tasks: 256 total,   3 running, 252 sleeping,   1 stopped,   0 zombie
%Cpu(s): 42.0 us, 13.37 sy,  0.0 ni, 44.63 id,  0.0 wa,  0.0 hi,  0.0 si
MiB Mem :  16042.0 total,   1337.0 free,   8192.0 used,   6513.0 buff/cache
MiB Swap:   8192.0 total,   8192.0 free,      0.0 used.   7777.0 avail Mem

    PID USER      PR  NI    VIRT    RES    SHR S  %CPU  %MEM     TIME+ COMMAND
   1337 gabriele  20   0 1337000 420420  69420 S  42.0  13.37   0:13.37 node
   4200 gabriele  20   0  512000 128000  64000 R  13.37  0.80    0:04.20 chrome
   6969 gabriele  20   0  256000  64000  32000 S   6.9   0.40    0:06.09 spotify`;
        break;

      case 'ps':
        output = `  PID TTY          TIME CMD
 1337 pts/0    00:00:42 bash
 4200 pts/0    00:00:13 node
 6969 pts/0    00:00:01 ps
 
(All processes are fictional. Any resemblance to real processes is purely coincidental.)`;
        break;

      case 'kill':
        output = "kill: I'm a lover, not a fighter. 💕\n(Also, no real processes here to kill)";
        break;

      case 'killall':
        output = "killall: Calm down, John Wick. This is just a demo terminal.";
        break;

      case 'wget':
      case 'curl':
        if (!argsStr) {
          output = `${command}: missing URL`;
        } else {
          output = `${command}: Network is unreachable\n(This terminal is in a sandbox. No real network access!)`;
        }
        break;

      case 'ssh':
        output = `ssh: connect to host ${argsStr || 'unknown'} port 22: Connection refused

(Did you really expect to SSH from a browser? Bold move.)`;
        break;

      case 'telnet':
        output = "telnet: It's not 1995 anymore. Use SSH.\n(Also, this is a demo terminal)";
        break;

      case 'apt':
      case 'apt-get':
      case 'yum':
      case 'dnf':
      case 'pacman':
      case 'brew':
        output = `${command}: command not found

This is a web terminal, not a real package manager.
But if it helps, try: npm install happiness --save`;
        break;

      case 'npm':
        if (argsStr.includes('install')) {
          output = `npm WARN deprecated happiness@1.0.0: this package is no longer supported

added 9999 packages, and audited 10000 packages in 42s

69 packages are looking for funding
  run \`npm fund\` for details

found 420 vulnerabilities (13 low, 37 moderate, 370 high)
  run \`npm audit fix\` to fix them, or \`npm audit\` for details

(Welcome to JavaScript dependency hell! 🔥)`;
        } else {
          output = "npm: This isn't a real terminal, but I appreciate your enthusiasm for JavaScript.";
        }
        break;

      case 'python':
      case 'python3':
      case 'node':
        output = `${command}: interpreter not found

This is a simulated terminal. For real coding, try:
- Replit
- CodeSandbox
- Or just open your favorite IDE`;
        break;

      case 'git':
        if (argsStr === 'gud') {
          output = "git: 'gud' is not a git command. See 'git --help'.\n\nDid you mean this?\n\tgit good (install skill tree first)";
        } else if (argsStr === 'blame') {
          output = "git blame: It's always the intern's fault. Always.";
        } else if (argsStr === 'commit -m "fix"') {
          output = "Please write better commit messages. Your future self will thank you.";
        } else {
          output = `git: not a real git repository (or any of the parent directories): .git\n(But your commitment to version control is appreciated!)`;
        }
        break;

      case 'lolcat':
        output = "🌈 M E O W 🌈\n(lolcat would make this colorful, but you'll have to imagine it)";
        break;

      case 'figlet':
        if (!argsStr) {
          output = "Usage: figlet [OPTIONS] [message]";
        } else {
          output = `
 _   _ _____ _     _     ___  
| | | | ____| |   | |   / _ \\ 
| |_| |  _| | |   | |  | | | |
|  _  | |___| |___| |__| |_| |
|_| |_|_____|_____|_____\\___/ 
                              
(Not the real figlet, but close enough!)`;
        }
        break;

      case 'cmatrix':
        output = "🟢 Imagine green falling characters here...\n\n(For the real Matrix experience, try a dangerous command 😉)";
        break;

      case 'yes':
        output = "yes\nyes\nyes\nyes\nyes\n...\n(Stopped after 5 to save your sanity)";
        break;

      case 'no':
        output = "no\nno\nno\nno\nno\n...\n(You're a contrarian, I see)";
        break;

      case 'maybe':
        output = "Maybe. Maybe not. Who knows? ¯\\_(ツ)_/¯";
        break;

      case 'hello':
      case 'hi':
      case 'hey':
      case 'ciao':
        output = `${command === 'ciao' ? 'Ciao' : 'Hello'} there! 👋

Welcome to my portfolio. Feel free to explore!
Type 'help' for a list of commands.`;
        break;

      case 'thanks':
      case 'grazie':
      case 'thx':
        output = "You're welcome! 😊 Glad you stopped by.";
        break;

      case 'matrix':
        setShowMatrix(true);
        output = '';
        break;

      case '42':
        output = "The Answer to the Ultimate Question of Life, the Universe, and Everything.";
        break;

      case 'love':
        output = `
❤️  I love:
  - Clean code
  - Well-documented APIs
  - Friday deployments (just kidding, NEVER)
  - Coffee ☕
  - Open source
  - Users who read documentation`;
        break;

      case 'hate':
        output = `
💔 I strongly dislike:
  - "Works on my machine"
  - Undocumented code
  - Magic numbers
  - Tabs... wait no, spaces... wait...
  - Merge conflicts on Fridays`;
        break;

      case 'nmap':
        output = simulateNmap(argsStr);
        break;

      case 'hydra':
        output = simulateHydra(argsStr);
        break;

      case 'metasploit':
      case 'msfconsole':
        output = simulateMetasploit();
        break;

      case 'ffuf':
        output = simulateFfuf(argsStr);
        break;

      case 'wafwoof':
        output = simulateWafwoof(argsStr);
        break;

      case 'docker':
        output = simulateDocker(argsStr);
        break;

      case 'alias':
        output = `alias ll='ls -la'
alias la='ls -A'
alias l='ls -CF'
alias grep='grep --color=auto'
alias cls='clear'
alias please='sudo'
alias fuck='sudo $(history -p !!)'
alias yolo='git push --force'`;
        break;

      case 'please':
        output = getSudoResponse(sudoAttempts);
        setSudoAttempts(sudoAttempts + 1);
        break;

      case 'reboot':
      case 'shutdown':
      case 'poweroff':
        output = `${command}: Permission denied

Besides, who reboots a website? Just refresh the page!
(F5 or Cmd+R, you're welcome)`;
        break;

      case 'ascii':
        output = developerInfo.name ? asciiArt.welcome : "No ASCII art available";
        break;

      case 'motd':
        output = `
╔═══════════════════════════════════════════════════════════════════╗
║                                                                   ║
║   Welcome to Gabriele's Portfolio Terminal!                       ║
║                                                                   ║
║   "The only way to do great work is to love what you do."        ║
║                                        - Steve Jobs               ║
║                                                                   ║
║   Current mood: ☕ Caffeinated and ready to code                  ║
║                                                                   ║
╚═══════════════════════════════════════════════════════════════════╝`;
        break;

      case 'time':
        output = `Current time: ${new Date().toLocaleTimeString()}
Time zone: ${Intl.DateTimeFormat().resolvedOptions().timeZone}
Unix timestamp: ${Math.floor(Date.now() / 1000)}

Fun fact: It's always coffee time ☕`;
        break;

      case 'weather':
        output = `Weather in Verona, Italy:
🌤️  Partly cloudy, 15°C
💨 Wind: 10 km/h
💧 Humidity: 65%

(This is not real weather data. Check a real weather service!)`;
        break;

      case 'joke':
        const jokes = [
          "Why do programmers prefer dark mode? Because light attracts bugs! 🐛",
          "A SQL query walks into a bar, walks up to two tables and asks: 'Can I join you?'",
          "Why do Java developers wear glasses? Because they can't C#!",
          "There are only 10 types of people: those who understand binary and those who don't.",
          "A programmer's wife tells him: 'Go to the store and buy a loaf of bread. If they have eggs, buy a dozen.' He comes home with 12 loaves of bread.",
          "Why was the JavaScript developer sad? Because he didn't Node how to Express himself.",
          "What's a programmer's favorite hangout place? Foo Bar!",
          "Why do programmers hate nature? It has too many bugs.",
        ];
        output = jokes[Math.floor(Math.random() * jokes.length)];
        break;

      case 'xkcd':
        output = `
  ┌───────────────────────────────────┐
  │  XKCD #149 - Sandwich            │
  │                                   │
  │  "Make me a sandwich."           │
  │  "What? Make it yourself."       │
  │  "sudo make me a sandwich."      │
  │  "Okay."                         │
  │                                   │
  │  https://xkcd.com/149/           │
  └───────────────────────────────────┘`;
        break;

      default:
        // Check for some partial matches
        if (lowerCmd.startsWith('rm ')) {
          output = "rm: Permission denied. This terminal values file preservation. 📁";
        } else if (lowerCmd.includes('password') || lowerCmd.includes('passwd')) {
          output = "🔐 Nice try! Passwords are stored securely... in my head.";
        } else if (lowerCmd.includes('hack')) {
          output = hackerArt;
        } else {
          output = `Command not found: ${command}\n\n${getRandomInsult()}\n\nType 'help' for available commands.`;
        }
    }

    setHistory([...history, { command: trimmedCmd, output, path: currentPath }]);
    setCommandHistory([...commandHistory, trimmedCmd]);
    setHistoryIndex(-1);
  }, [history, commandHistory, currentPath, navigate, sudoAttempts]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (input.trim()) {
      executeCommand(input);
      setInput('');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (commandHistory.length > 0) {
        const newIndex = historyIndex < commandHistory.length - 1 ? historyIndex + 1 : historyIndex;
        setHistoryIndex(newIndex);
        setInput(commandHistory[commandHistory.length - 1 - newIndex] || '');
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (historyIndex > 0) {
        const newIndex = historyIndex - 1;
        setHistoryIndex(newIndex);
        setInput(commandHistory[commandHistory.length - 1 - newIndex] || '');
      } else {
        setHistoryIndex(-1);
        setInput('');
      }
    } else if (e.key === 'Tab') {
      e.preventDefault();
      // Simple tab completion for common commands
      const commands = ['help', 'ls', 'cd', 'pwd', 'whoami', 'cat', 'echo', 'clear', 'history', 
                       'date', 'nmap', 'hydra', 'metasploit', 'ffuf', 'wafwoof', 'docker',
                       'cowsay', 'fortune', 'coffee', 'hack', 'neofetch', 'joke'];
      const matches = commands.filter(cmd => cmd.startsWith(input.toLowerCase()));
      if (matches.length === 1) {
        setInput(matches[0]);
      } else if (matches.length > 1) {
        setHistory([...history, { 
          command: input, 
          output: `Possible completions: ${matches.join('  ')}`,
          path: currentPath 
        }]);
      }
    }
  };

  const handleCloseMatrix = useCallback(() => {
    setShowMatrix(false);
  }, []);

  return (
    <>
      {showMatrix && <MatrixEasterEgg onClose={handleCloseMatrix} />}
      <div 
        className={`terminal-container ${fullScreen ? 'terminal-fullscreen' : 'terminal-widget'}`}
        onClick={() => inputRef.current?.focus()}
      >
        <div className="terminal-header">
          <div className="terminal-buttons">
            <span className="terminal-button close"></span>
            <span className="terminal-button minimize"></span>
            <span className="terminal-button maximize"></span>
          </div>
          <div className="terminal-title">gabriele@node01:~</div>
        </div>
        
        <div className="terminal-output" ref={outputRef}>
          {history.map((item, index) => (
            <div key={index} className="terminal-line">
              {item.command && (
                <div className="terminal-input-line">
                  <span className="terminal-prompt">[gabriele@node01 {item.path || currentPath}]$ </span>
                  <span className="terminal-command">{item.command}</span>
                </div>
              )}
              {item.output && (
                <pre className="terminal-output-text">{item.output}</pre>
              )}
            </div>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="terminal-input-form">
          <span className="terminal-prompt">[gabriele@node01 {currentPath}]$ </span>
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            className="terminal-input"
            autoFocus
            spellCheck={false}
            autoComplete="off"
          />
          <span className="terminal-cursor">_</span>
        </form>
      </div>
    </>
  );
};

export default Terminal;
